/**
 * Helper functions for image processing and manipulation
 */

/**
 * Resizes an image file maintaining aspect ratio
 * @param {File} file - The original image file
 * @param {number} maxWidth - Maximum width for the resized image
 * @param {number} maxHeight - Maximum height for the resized image
 * @param {number} quality - Image quality (0-1), default 0.8
 * @returns {Promise<Blob>} Promise that resolves to the resized image blob
 */
export const resizeImage = (file, maxWidth, maxHeight, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calcular nuevas dimensiones manteniendo proporción
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Dibujar imagen redimensionada
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convertir a blob
      canvas.toBlob(resolve, file.type, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Creates a File object from a blob with a custom filename
 * @param {Blob} blob - The blob to convert to File
 * @param {string} originalFileName - The original filename
 * @param {string} suffix - Suffix to add to the filename (default: '')
 * @returns {File} New File object with the specified name
 */
export const createFileFromBlob = (blob, originalFileName, suffix = '') => {
  const nameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
  const extension = originalFileName.split('.').pop();
  const newFileName = `${nameWithoutExt}${suffix}.${extension}`;
  
  return new File([blob], newFileName, { type: blob.type });
};

/**
 * Resizes an image and returns a File object
 * @param {File} file - The original image file
 * @param {number} maxWidth - Maximum width for the resized image
 * @param {number} maxHeight - Maximum height for the resized image
 * @param {number} quality - Image quality (0-1), default 0.8
 * @param {string} suffix - Suffix to add to the filename (default: '_resized')
 * @returns {Promise<File>} Promise that resolves to the resized image file
 */
export const resizeImageToFile = async (file, maxWidth, maxHeight, quality = 0.8, suffix = '_resized') => {
  const resizedBlob = await resizeImage(file, maxWidth, maxHeight, quality);
  return createFileFromBlob(resizedBlob, file.name, suffix);
};

/**
 * Creates an avatar version of an image (128x128)
 * @param {File} file - The original image file
 * @param {number} quality - Image quality (0-1), default 0.8
 * @returns {Promise<File>} Promise that resolves to the avatar image file
 */
export const createAvatarImage = async (file, quality = 0.8) => {
  return resizeImageToFile(file, 128, 128, quality, '_avatar');
};

/**
 * Creates a thumbnail version of an image (256x256)
 * @param {File} file - The original image file
 * @param {number} quality - Image quality (0-1), default 0.8
 * @returns {Promise<File>} Promise that resolves to the thumbnail image file
 */
export const createThumbnailImage = async (file, quality = 0.8) => {
  return resizeImageToFile(file, 256, 256, quality, '_thumb');
};
